// src/features/clients/ClientsAdmin.jsx
import React, { useEffect, useState } from "react";
import {
  apiGet,
  apiDel,
  apiPostJSON,
  apiPutJSON,
  apiPostForm,
  apiPutForm,
  API_BASE,
} from "../../admin/api";
import "./clients-admin.css";

export default function ClientsAdmin() {
  /* =========================
   *  STATE
   * ========================= */
  const [categories, setCategories] = useState([]);
  const [logos, setLogos] = useState([]);
  const [loading, setLoading] = useState(false);

  // Category form
  const [catForm, setCatForm] = useState({
    id: null,
    name: "",
    description: "",
    sort_order: "",
  });

  // Logo form
  const [logoForm, setLogoForm] = useState({
    id: null,
    category_id: "",
    client_name: "",
    status: "published",
    sort_order: "",
  });
  const [logoImage, setLogoImage] = useState(null);
  const [showLogoModal, setShowLogoModal] = useState(false);

  // Filters for logo list
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");

  /* =========================
   *  LOADERS
   * ========================= */

  const loadCategories = async () => {
    const data = await apiGet("/api/clients/categories");
    setCategories(Array.isArray(data) ? data : []);
  };

  const loadLogos = async () => {
    const qs = new URLSearchParams();
    if (filterCategory !== "all" && filterCategory) {
      qs.set("category_id", filterCategory);
    }
    if (filterStatus !== "all") {
      qs.set("status", filterStatus);
    }

    const url =
      qs.toString().length > 0
        ? `/api/clients/logos?${qs.toString()}`
        : "/api/clients/logos";

    const data = await apiGet(url);
    setLogos(Array.isArray(data) ? data : []);
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      await Promise.all([loadCategories(), loadLogos()]);
    } catch (e) {
      alert(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // reload logos when filters change
  useEffect(() => {
    loadLogos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterCategory, filterStatus]);

  /* =========================
   *  CATEGORY HANDLERS
   * ========================= */

  const resetCatForm = () => {
    setCatForm({ id: null, name: "", description: "", sort_order: "" });
  };

  const startEditCategory = (cat) => {
    setCatForm({
      id: cat.id,
      name: cat.name || "",
      description: cat.description || "",
      sort_order:
        cat.sort_order === null || cat.sort_order === undefined
          ? ""
          : String(cat.sort_order),
    });
  };

  const saveCategory = async (e) => {
    e.preventDefault();

    if (!catForm.name.trim()) {
      alert("Category name is required");
      return;
    }

    const body = {
      name: catForm.name,
      description: catForm.description || "",
      sort_order:
        catForm.sort_order === "" ? 0 : Number(catForm.sort_order) || 0,
    };

    try {
      if (catForm.id) {
        await apiPutJSON(`/api/clients/categories/${catForm.id}`, body);
      } else {
          await apiPostJSON(`/api/clients/categories`, body);
      }

      resetCatForm();
      await loadCategories();
    } catch (err) {
      alert(err.message);
    }
  };

  const deleteCategory = async (id) => {
    if (
      !window.confirm(
        "Delete this category? Any logos under it will also be removed."
      )
    )
      return;

    try {
      await apiDel(`/api/clients/categories/${id}`);
      // if the current filter was this category, reset filter
      if (filterCategory === String(id)) {
        setFilterCategory("all");
      }
      await loadAll();
    } catch (err) {
      alert(err.message);
    }
  };

  /* =========================
   *  LOGO HANDLERS
   * ========================= */

  const openNewLogo = () => {
    setLogoForm({
      id: null,
      category_id: "",
      client_name: "",
      status: "published",
      sort_order: "",
    });
    setLogoImage(null);
    setShowLogoModal(true);
  };

  const openEditLogo = (logo) => {
    setLogoForm({
      id: logo.id,
      category_id: logo.category_id ? String(logo.category_id) : "",
      client_name: logo.client_name || "",
      status: logo.status || "published",
      sort_order:
        logo.sort_order === null || logo.sort_order === undefined
          ? ""
          : String(logo.sort_order),
    });
    setLogoImage(null); // only change if user picks a new file
    setShowLogoModal(true);
  };

  const closeLogoModal = () => {
    setShowLogoModal(false);
    setLogoImage(null);
  };

  const saveLogo = async (e) => {
    e.preventDefault();

    if (!logoForm.category_id) {
      alert("Please select a category");
      return;
    }

    if (!logoForm.id && !logoImage) {
      alert("Please select a logo image");
      return;
    }

    const body = {
      category_id: logoForm.category_id,
      client_name: logoForm.client_name || "",
      status: logoForm.status || "published",
      sort_order:
        logoForm.sort_order === "" ? 0 : Number(logoForm.sort_order) || 0,
    };

    if (logoImage) {
      body.image = logoImage;
    }

    try {
      if (logoForm.id) {
        await apiPutForm(`/api/clients/logos/${logoForm.id}`, body);
      } else {
        await apiPostForm("/api/clients/logos", body);
      }

      closeLogoModal();
      await loadLogos();
    } catch (err) {
      alert(err.message);
    }
  };

  const deleteLogo = async (id) => {
    if (!window.confirm("Delete this logo?")) return;
    try {
      await apiDel(`/api/clients/logos/${id}`);
      await loadLogos();
    } catch (err) {
      alert(err.message);
    }
  };

  /* =========================
   *  RENDER
   * ========================= */

  return (
    <div className="clients-admin">
      {/* Toolbar */}
      <div className="clients-toolbar">
        <h2 className="clients-toolbar__title">Clients</h2>
        <div className="clients-toolbar__actions">
          <button className="clients-btn" onClick={openNewLogo}>
            + New Logo
          </button>
        </div>
      </div>

      {loading ? (
        <div className="clients-empty">Loading…</div>
      ) : (
        <div className="clients-layout">
          {/* =================== CATEGORIES PANEL =================== */}
          <section className="clients-panel">
            <h3 className="clients-panel__title">Client Categories</h3>

            <form className="clients-form" onSubmit={saveCategory}>
              <label className="clients-field">
                <span className="clients-field__label">Name *</span>
                <input
                  className="clients-field__input"
                  value={catForm.name}
                  onChange={(e) =>
                    setCatForm((f) => ({ ...f, name: e.target.value }))
                  }
                  placeholder="e.g., IT & Networking"
                  required
                />
              </label>

              <label className="clients-field">
                <span className="clients-field__label">Description</span>
                <textarea
                  className="clients-field__input"
                  rows={3}
                  value={catForm.description}
                  onChange={(e) =>
                    setCatForm((f) => ({
                      ...f,
                      description: e.target.value,
                    }))
                  }
                  placeholder="Short description for this client segment…"
                />
              </label>

              <label className="clients-field clients-field--inline">
                <span className="clients-field__label">Sort Order</span>
                <input
                  className="clients-field__input"
                  type="number"
                  value={catForm.sort_order}
                  onChange={(e) =>
                    setCatForm((f) => ({
                      ...f,
                      sort_order: e.target.value,
                    }))
                  }
                  placeholder="0"
                />
              </label>

              <div className="clients-form__actions">
                {catForm.id && (
                  <button
                    type="button"
                    className="clients-btn clients-btn--sm"
                    onClick={resetCatForm}
                  >
                    Cancel edit
                  </button>
                )}
                <button
                  type="submit"
                  className="clients-btn clients-btn--primary"
                >
                  {catForm.id ? "Save Category" : "Add Category"}
                </button>
              </div>
            </form>

            <div className="clients-cat-list">
              {categories.length === 0 ? (
                <div className="clients-empty">No categories yet.</div>
              ) : (
                categories.map((cat) => (
                  <div key={cat.id} className="clients-cat-item">
                    <div className="clients-cat-item__main">
                      <div className="clients-cat-item__name">{cat.name}</div>
                      {cat.description && (
                        <div className="clients-cat-item__desc">
                          {cat.description}
                        </div>
                      )}
                    </div>
                    <div className="clients-cat-item__meta">
                      <span className="clients-chip">
                        Order: {cat.sort_order ?? 0}
                      </span>
                      <div className="clients-cat-item__actions">
                        <button
                          className="clients-btn clients-btn--ghost clients-btn--sm"
                          onClick={() => startEditCategory(cat)}
                        >
                          Edit
                        </button>
                        <button
                          className="clients-btn clients-btn--danger clients-btn--sm"
                          onClick={() => deleteCategory(cat.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </section>

          {/* =================== LOGOS PANEL =================== */}
          <section className="clients-panel">
            <div className="clients-panel__head">
              <h3 className="clients-panel__title">Client Logos</h3>

              <div className="clients-filters">
                <select
                  className="clients-field__input clients-field__input--sm"
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                >
                  <option value="all">All Categories</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>

                <select
                  className="clients-field__input clients-field__input--sm"
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <option value="all">All Status</option>
                  <option value="published">Published</option>
                  <option value="draft">Draft</option>
                </select>
              </div>
            </div>

            {logos.length === 0 ? (
              <div className="clients-empty-block">
                <div className="clients-empty-block__icon">🏢</div>
                <div className="clients-empty-block__title">No logos yet</div>
                <div className="clients-empty-block__desc">
                  Click “New Logo” to upload logos and assign them to
                  categories.
                </div>
                <button
                  className="clients-btn clients-btn--primary"
                  onClick={openNewLogo}
                >
                  + New Logo
                </button>
              </div>
            ) : (
              <div className="clients-logo-grid">
                {logos.map((logo) => {
                  const src = logo.image ? `${API_BASE}${logo.image}` : null;
                  return (
                    <article key={logo.id} className="clients-logo-card">
                      <div className="clients-logo-card__thumb">
                        {src ? (
                      
                          <img
                            className="clients-logo-thumb"
                            src={src}
                            alt={logo.client_name || "Client"}
                          />
                        ) : (
                          <div className="clients-logo-card__thumb--empty">
                            No Image
                          </div>
                        )}
                      </div>
                      <div className="clients-logo-card__body">
                        <div className="clients-logo-card__top">
                          <span className="clients-chip">
                            {logo.category?.name || "No category"}
                          </span>
                          <span
                            className={
                              logo.status === "published"
                                ? "clients-status clients-status--pub"
                                : "clients-status clients-status--draft"
                            }
                          >
                            {logo.status}
                          </span>
                        </div>
                        {logo.client_name && (
                          <div className="clients-logo-card__title">
                            {logo.client_name}
                          </div>
                        )}
                        <div className="clients-logo-card__meta">
                          Order: {logo.sort_order ?? 0}
                        </div>
                        <div className="clients-logo-card__actions">
                          <button
                            className="clients-btn clients-btn--ghost clients-btn--sm"
                            onClick={() => openEditLogo(logo)}
                          >
                            Edit
                          </button>
                          <button
                            className="clients-btn clients-btn--danger clients-btn--sm"
                            onClick={() => deleteLogo(logo.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      )}

      {/* =================== LOGO MODAL =================== */}
      {showLogoModal && (
        <div className="clients-modal" role="dialog" aria-modal="true">
          <div
            className="clients-modal__backdrop"
            onClick={closeLogoModal}
          />
          <div className="clients-modal__card" role="document">
            <div className="clients-modal__head">
              <h3 className="clients-modal__title">
                {logoForm.id ? "Edit Logo" : "New Logo"}
              </h3>
              <button
                className="clients-btn clients-btn--danger clients-btn--sm"
                onClick={closeLogoModal}
              >
                ×
              </button>
            </div>

            <form className="clients-form" onSubmit={saveLogo}>
              <label className="clients-field">
                <span className="clients-field__label">Category *</span>
                <select
                  className="clients-field__input"
                  value={logoForm.category_id}
                  onChange={(e) =>
                    setLogoForm((f) => ({
                      ...f,
                      category_id: e.target.value,
                    }))
                  }
                  required
                >
                  <option value="">Select category…</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="clients-field">
                <span className="clients-field__label">Client Name</span>
                <input
                  className="clients-field__input"
                  value={logoForm.client_name}
                  onChange={(e) =>
                    setLogoForm((f) => ({
                      ...f,
                      client_name: e.target.value,
                    }))
                  }
                  placeholder="Optional label, e.g., Everest Hotel"
                />
              </label>

              <div className="clients-row">
                <label className="clients-field clients-grow">
                  <span className="clients-field__label">Status</span>
                  <select
                    className="clients-field__input"
                    value={logoForm.status}
                    onChange={(e) =>
                      setLogoForm((f) => ({
                        ...f,
                        status: e.target.value,
                      }))
                    }
                  >
                    <option value="published">published</option>
                    <option value="draft">draft</option>
                  </select>
                </label>

                <label className="clients-field clients-grow">
                  <span className="clients-field__label">Sort Order</span>
                  <input
                    className="clients-field__input"
                    type="number"
                    value={logoForm.sort_order}
                    onChange={(e) =>
                      setLogoForm((f) => ({
                        ...f,
                        sort_order: e.target.value,
                      }))
                    }
                    placeholder="0"
                  />
                </label>
              </div>

              <label className="clients-field">
                <span className="clients-field__label">Logo Image</span>
                <input
                  className="clients-field__input"
                  type="file"
                  accept="image/*"
                  onChange={(e) =>
                    setLogoImage(e.target.files?.[0] || null)
                  }
                />
              </label>

              {/* existing preview (only when editing and no new file picked) */}
              {logoForm.id && !logoImage && (() => {
                const existing = logos.find((l) => l.id === logoForm.id);
                if (!existing || !existing.image) return null;
                return (
                  <div className="clients-logo-preview">
                    <img
                      className="clients-logo-preview__img"
                      src={`${API_BASE}${existing.image}`}
                      alt={existing.client_name || "logo"}
                    />
                  </div>
                );
              })()}

              <div className="clients-modal__actions">
                <button
                  type="button"
                  className="clients-btn clients-btn--sm"
                  onClick={closeLogoModal}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="clients-btn clients-btn--primary"
                >
                  {logoForm.id ? "Save Logo" : "Add Logo"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
